import dayjs from 'dayjs'
import { customDayjs } from '../../utils/date'

/**
 * 연간 결제 계산 (aipark-front #263, BE aivatar #357).
 *
 * 연간은 "다른 플랜"이 아니라 "같은 플랜 + interval_month=12"다. Plan 에는 주기·연간가
 * 필드가 없고, 연간 청구액은 서버가 plan.amount × 12 × (1 − 할인율) 로 파생한다.
 * 이 파일은 그 계산을 화면 표시용으로만 재현한다. 실제 청구액은 언제나
 * /payments/checkout 의 chargeAmount 다.
 */

export const MONTHLY = 1
export const ANNUAL = 12

export type SubscriptionInterval = typeof MONTHLY | typeof ANNUAL

/**
 * BE 가 /v1/plans 에 annualDiscountRate 를 싣기 전까지 쓰는 임시값.
 *
 * 서버 설정(payment.subscription.annual-discount-rate, dev ConfigMap
 * PAYMENT_ANNUAL_DISCOUNT_RATE=0.2)과 같은 값이어야 한다. 이 상수는 플랜 모달의
 * 표시가에만 쓰이고 실제 청구는 서버가 하므로, 어긋나도 결제 금액이 틀리지는 않는다.
 * 체크아웃에서 서버 값과 대조해 경고를 띄운다(assertAnnualAmountMatchesServer).
 */
const FALLBACK_ANNUAL_DISCOUNT_RATE = 0.2

type PlanLike = {
  price: number
  amount: number
  /** BE 가 /v1/plans 에 실으면 이쪽이 우선한다 */
  annualAmount?: number | null
  annualDiscountRate?: number | null
}

/** 12만 연간으로 본다. 해지 예약 행에 마커로 120 이 들어가므로 산술 판정을 쓰지 않는다 */
export function isAnnual(intervalMonth?: number | null) {
  return intervalMonth === ANNUAL
}

export function billingMonths(intervalMonth?: number | null): SubscriptionInterval {
  return isAnnual(intervalMonth) ? ANNUAL : MONTHLY
}

export function getAnnualDiscountRate(plan?: PlanLike | null) {
  return plan?.annualDiscountRate ?? FALLBACK_ANNUAL_DISCOUNT_RATE
}

/** 할인율을 퍼센트 정수로. 토글·뱃지 문구용 */
export function getAnnualDiscountPercent(plan?: PlanLike | null) {
  return Math.round(getAnnualDiscountRate(plan) * 100)
}

/** 서버와 같은 반올림. BigDecimal.setScale(0, HALF_UP) 에 대응한다 */
function annualOf(monthly: number, rate: number) {
  return Math.round(monthly * ANNUAL * (1 - rate))
}

/** 연간 청구액 (부가세 포함). 체크아웃·결제 금액 비교용 */
export function getAnnualAmount(plan: PlanLike) {
  return plan.annualAmount ?? annualOf(plan.amount, getAnnualDiscountRate(plan))
}

/** 연간 청구액 (부가세 별도). 플랜 모달은 부가세 별도로 표기한다 */
export function getAnnualPrice(plan: PlanLike) {
  return annualOf(plan.price, getAnnualDiscountRate(plan))
}

/** 연간의 월 환산가 (부가세 별도). 월간 정가와 비교하기 위한 값이며 청구 단위가 아니다 */
export function getAnnualMonthlyPrice(plan: PlanLike) {
  return Math.round(getAnnualPrice(plan) / ANNUAL)
}

/** 표시가가 서버 정가와 갈라지면 할인율 상수가 서버 설정과 벌어진 것이다 */
export function assertAnnualAmountMatchesServer(plan: PlanLike | undefined, serverPrice?: number) {
  if (!plan || serverPrice == null) return
  const expected = getAnnualAmount(plan)
  if (expected !== Math.round(serverPrice)) {
    // eslint-disable-next-line no-console
    console.warn(
      `[annual] 연간 표시가가 서버 정가와 다릅니다. FE ${expected} / BE ${serverPrice}. ` +
        'FALLBACK_ANNUAL_DISCOUNT_RATE 와 서버 PAYMENT_ANNUAL_DISCOUNT_RATE 를 맞춰야 합니다.',
    )
  }
}

/**
 * anchor 일자에 맞춰 months 개월 뒤. 31일 계약이 2월엔 말일, 3월엔 다시 31일이 되도록
 * 한 달씩 전진하며 매번 앵커 일자로 되돌린다. plusMonths(n) 한 번으로 대신할 수 없다.
 * BE SubscriptionPeriod.anchoredDate 와 같은 규칙이다.
 */
function anchoredDate(anchor: dayjs.Dayjs, from: dayjs.Dayjs, months: number) {
  let date = from
  for (let i = 0; i < months; i += 1) {
    date = date.add(1, 'month')
    date = date.date(Math.min(anchor.date(), date.daysInMonth()))
  }
  return date
}

/**
 * 전환 시점에 아직 시작하지 않은 달의 수 (BE SubscriptionPeriod.unelapsedMonths 복제).
 *
 * 진행 중인 달은 크레딧 이월(현물)로 보상되므로 미도래에 넣지 않는다. 월 구독은 항상 0.
 * 응답에 개월 수가 없어 화면이 "남은 기간 정산 (N개월)" 을 그리려면 여기서 세야 한다.
 */
export function unelapsedMonths(subscription?: {
  startedAt?: string | null
  subscribedAt?: string | null
  intervalMonth?: number | null
}) {
  const months = billingMonths(subscription?.intervalMonth)
  if (months === MONTHLY || !subscription?.startedAt) return 0

  const startedAt = customDayjs(subscription.startedAt).startOf('day')
  const anchor = customDayjs(subscription.subscribedAt ?? subscription.startedAt).startOf('day')
  const today = customDayjs().startOf('day')

  let currentIndex = 0
  for (let index = 1; index < months; index += 1) {
    if (!anchoredDate(anchor, startedAt, index).isAfter(today)) {
      currentIndex = index
    }
  }
  return months - 1 - currentIndex
}

/**
 * 청구서·결제 이력처럼 intervalMonth 가 응답에 실려 있다는 보장이 없는 곳의 주기 판정.
 *
 * intervalMonth 가 오면 그대로 믿고, 없으면 구독 구간 길이로 되짚는다. 월 구독의
 * 구간은 0~1개월, 연 구독은 11~12개월이라 중간값 6으로 가른다.
 */
export function isAnnualPeriod(
  subscription?: {
    intervalMonth?: number | null
    startedAt?: string | null
    endAt?: string | null
  } | null,
) {
  if (subscription?.intervalMonth != null) return isAnnual(subscription.intervalMonth)
  if (!subscription?.startedAt || !subscription?.endAt) return false
  return customDayjs(subscription.endAt).diff(customDayjs(subscription.startedAt), 'month') >= 6
}
